import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button,
  Input
} from '@city-sage/aurum-avis-labs-components';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  TelegramIcon,
  LinkedinIcon,
  EmailIcon
} from 'react-share';
import { Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SocialShareDialogProps {
  url: string;
  title: string;
  description?: string;
  hashtags?: string[];
  children: React.ReactNode;
}

export function SocialShareDialog({
  url,
  title,
  description,
  hashtags = [],
  children
}: SocialShareDialogProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const shareUrl = url;
  const shareTitle = title;
  const shareDescription = description || `Check out this amazing itinerary: ${title}`;
  const shareHashtags = hashtags.length > 0 ? hashtags : ['CitySage', 'Travel', 'Itinerary'];


  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  };

  const iconSize = 48;
  const iconClassName = "!rounded-xl hover:scale-105 transition-transform duration-200";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            {t('common.share.title', 'Share Itinerary')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('common.share.description', 'Share this amazing itinerary with your friends')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Social Media Platforms */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-gray-700">
              {t('common.share.socialPlatforms', 'Social Platforms')}
            </h4>
            <div className="grid grid-cols-4 gap-4 justify-items-center">
              <div className="flex flex-col items-center gap-2">
                <FacebookShareButton
                  url={shareUrl}
                  hashtag={`#${shareHashtags[0]}`}
                  className={iconClassName}
                >
                  <FacebookIcon size={iconSize} className={iconClassName} />
                </FacebookShareButton>
                <span className="text-xs text-gray-600">Facebook</span>
              </div>

              <div className="flex flex-col items-center gap-2">
                <TwitterShareButton
                  url={shareUrl}
                  title={shareDescription}
                  hashtags={shareHashtags}
                  className={iconClassName}
                >
                  <TwitterIcon size={iconSize} className={iconClassName} />
                </TwitterShareButton>
                <span className="text-xs text-gray-600">Twitter</span>
              </div>

              <div className="flex flex-col items-center gap-2">
                <WhatsappShareButton
                  url={shareUrl}
                  title={shareDescription}
                  className={iconClassName}
                >
                  <WhatsappIcon size={iconSize} className={iconClassName} />
                </WhatsappShareButton>
                <span className="text-xs text-gray-600">WhatsApp</span>
              </div>

              <div className="flex flex-col items-center gap-2">
                <LinkedinShareButton
                  url={shareUrl}
                  title={shareTitle}
                  summary={shareDescription}
                  className={iconClassName}
                >
                  <LinkedinIcon size={iconSize} className={iconClassName} />
                </LinkedinShareButton>
                <span className="text-xs text-gray-600">LinkedIn</span>
              </div>

              <div className="flex flex-col items-center gap-2">
                <TelegramShareButton
                  url={shareUrl}
                  title={shareDescription}
                  className={iconClassName}
                >
                  <TelegramIcon size={iconSize} className={iconClassName} />
                </TelegramShareButton>
                <span className="text-xs text-gray-600">Telegram</span>
              </div>

              <div className="flex flex-col items-center gap-2">
                <EmailShareButton
                  url={shareUrl}
                  subject={shareTitle}
                  body={shareDescription}
                  className={iconClassName}
                >
                  <EmailIcon size={iconSize} className={iconClassName} />
                </EmailShareButton>
                <span className="text-xs text-gray-600">Email</span>
              </div>

            </div>
          </div>

          {/* Copy Link */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-gray-700">
              {t('common.share.copyLink', 'Copy Link')}
            </h4>
            <div className="flex gap-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1 text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyLink}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            {copied && (
              <p className="text-xs text-green-600 mt-1">
                {t('common.share.linkCopied', 'Link copied to clipboard!')}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 